import React from 'react';
import { Upload, UserCheck, Handshake, Shield } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: Upload,
      title: "Upload Content",
      description: "Creators upload content or link their streams to the platform",
      color: "cyan"
    },
    {
      number: "02",
      icon: UserCheck,
      title: "Log Appearances",
      description: "People appearing in the content can be logged or verified automatically",
      color: "blue"
    },
    {
      number: "03",
      icon: Handshake,
      title: "Manage Agreements",
      description: "Any compensation or agreements are handled directly through the platform",
      color: "purple"
    },
    {
      number: "04",
      icon: Shield,
      title: "Secure Records",
      description: "Both parties have a secure record that prevents future disputes",
      color: "pink"
    }
  ];

  const colorClasses = {
    cyan: {
      bg: "from-cyan-500/10 to-blue-500/10",
      border: "border-cyan-500/30",
      text: "text-cyan-400",
      glow: "bg-cyan-500/20"
    },
    blue: {
      bg: "from-blue-500/10 to-purple-500/10",
      border: "border-blue-500/30",
      text: "text-blue-400",
      glow: "bg-blue-500/20"
    },
    purple: {
      bg: "from-purple-500/10 to-pink-500/10",
      border: "border-purple-500/30",
      text: "text-purple-400",
      glow: "bg-purple-500/20"
    },
    pink: {
      bg: "from-pink-500/10 to-red-500/10",
      border: "border-pink-500/30",
      text: "text-pink-400",
      glow: "bg-pink-500/20"
    }
  };

  return (
    <section id="how-it-works" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0a0a2e]"></div>
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium inline-block backdrop-blur-sm">
            Simple Process
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            How It
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent"> Works</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Four simple steps to protect your content and ensure fair compensation
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const colors = colorClasses[step.color];
            
            return (
              <div key={index} className="relative group">
                {/* Connector Line (hidden on mobile and last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-gray-700 to-transparent"></div>
                )}

                <div className={`relative bg-gradient-to-br ${colors.bg} backdrop-blur-sm border ${colors.border} rounded-2xl p-6 h-full transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}>
                  {/* Step Number */}
                  <div className={`absolute -top-4 -right-4 w-12 h-12 rounded-full bg-gradient-to-br ${colors.bg} border ${colors.border} flex items-center justify-center backdrop-blur-sm`}>
                    <span className={`text-lg font-bold ${colors.text}`}>{step.number}</span>
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${colors.bg} border ${colors.border} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-8 h-8 ${colors.text}`} />
                  </div>

                  {/* Content */}
                  <h3 className={`text-2xl font-bold ${colors.text} mb-3`}>
                    {step.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Decorative Glow */}
                  <div className={`absolute -bottom-2 -right-2 w-20 h-20 ${colors.glow} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Visual Flow Diagram */}
        <div className="relative mt-20 p-12 rounded-3xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 border border-gray-700/50 backdrop-blur-sm overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500"></div>
          </div>

          <div className="relative z-10 text-center space-y-6">
            <h3 className="text-3xl font-bold text-white mb-8">
              End-to-End Protection
            </h3>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex-1 text-center p-6 rounded-2xl bg-cyan-500/10 border border-cyan-500/30">
                <div className="text-4xl mb-2">🎥</div>
                <p className="text-cyan-400 font-bold">Creator Uploads</p>
                <p className="text-gray-400 text-sm">Content & Stream Links</p>
              </div>

              <div className="text-gray-500 text-2xl rotate-90 md:rotate-0">→</div>

              <div className="flex-1 text-center p-6 rounded-2xl bg-purple-500/10 border border-purple-500/30">
                <div className="text-4xl mb-2">✓</div>
                <p className="text-purple-400 font-bold">Verification</p>
                <p className="text-gray-400 text-sm">Automated Appearance Detection</p>
              </div>

              <div className="text-gray-500 text-2xl rotate-90 md:rotate-0">→</div>

              <div className="flex-1 text-center p-6 rounded-2xl bg-pink-500/10 border border-pink-500/30">
                <div className="text-4xl mb-2">🔒</div>
                <p className="text-pink-400 font-bold">Secured</p>
                <p className="text-gray-400 text-sm">Dispute-Proof Records</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
